import React, { createContext, useContext, useState, useEffect } from 'react';
import {authapi} from '../config/API_URL';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check localStorage for cached user and org data
        const cachedUser = localStorage.getItem('user');
        const cachedOrg = localStorage.getItem('org');
        const cachedToken = localStorage.getItem('token');
        console.log('_______LETS SEE ')
        if (cachedUser && cachedOrg && cachedToken) {
          setUser(JSON.parse(cachedUser));
          setOrg(JSON.parse(cachedOrg));
          setIsAuthenticated(true);

          // Optional: Verify token with backend
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authapi.post('/auth/compliance/login', {
        email,
        password,
      });

      if (response.data.success) {
        const { user: userData, org: orgData, token } = response.data;
        
        // Store in state
        setUser(userData);
        setOrg(orgData);
        setIsAuthenticated(true);
        
        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('org', JSON.stringify(orgData));
        localStorage.setItem('token', token);
        
        return { success: true };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please check your credentials.',
      };
    }
  };

  const signup = async (signupData) => {
    try {
      const response = await authapi.post('/auth/compliance/signup', {
        username: signupData.username,
        email: signupData.email,
        password: signupData.password,
        dispensary_name: signupData.dispensaryName,
        state: signupData.state,
        license_type: signupData.licenseType,
        address: signupData.address,
      });

      if (response.data.success) {
        const { user: userData, org: orgData, token } = response.data;
        
        // Store in state
        setUser(userData);
        setOrg(orgData);
        setIsAuthenticated(true);
        
        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('org', JSON.stringify(orgData));
        localStorage.setItem('token', token);
        
        return { success: true };
      }
    } catch (error) {
      console.error('Signup failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Signup failed. Please try again.',
      };
    }
  };

  const logout = () => {
    setUser(null);
    setOrg(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('org');
    localStorage.removeItem('token');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const updateOrg = (updatedOrgData) => {
    setOrg(updatedOrgData);
    localStorage.setItem('org', JSON.stringify(updatedOrgData));
  };

  // Join organization via invite (if you implement this feature)
  const joinOrganization = async (inviteCode) => {
    try {
      const response = await authapi.post('/auth/compliance/join', {
        invite_code: inviteCode,
      });

      if (response.data.success) {
        const { org: orgData } = response.data;
        setOrg(orgData);
        localStorage.setItem('org', JSON.stringify(orgData));
        return { success: true };
      }
    } catch (error) {
      console.error('Join organization failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to join organization.',
      };
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        org,
        loading,
        isAuthenticated,
        login,
        signup,
        logout,
        updateUser,
        updateOrg,
        joinOrganization,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};




